export const PRIVATE_MEDIA_BUCKET='dymistrz-private-media';
export const PRIVATE_MEDIA_MAX_IMAGE_BYTES=5*1024*1024;

const MIME_EXTENSIONS=Object.freeze({
  'image/jpeg':'jpg',
  'image/png':'png',
  'image/webp':'webp'
});

function required(value,label,max=512){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function safeError(error,code){
  const wrapped=new Error(code);
  wrapped.code=String(error?.code||code).slice(0,80);
  wrapped.cause=error;
  return wrapped;
}

export function validatePrivateMediaImage({mimeType,sizeBytes,maxBytes=PRIVATE_MEDIA_MAX_IMAGE_BYTES}={}){
  const mime=String(mimeType||'').trim().toLowerCase();
  const extension=MIME_EXTENSIONS[mime];
  if(!extension)throw new TypeError('Unsupported private media image type');
  const size=Number(sizeBytes);
  if(!Number.isInteger(size)||size<1||size>maxBytes){
    throw new TypeError('Private media image size is outside the supported range');
  }
  return Object.freeze({mimeType:mime,extension,sizeBytes:size});
}

export function assertOwnedPrivateMediaPath(path,{userId}={}){
  const owner=required(userId,'userId',128);
  const value=required(path,'path',1024);
  if(value.startsWith('/')||value.includes('\\'))throw new TypeError('Invalid private media path');
  const segments=value.split('/');
  if(segments.some(segment=>!segment||segment==='.'||segment==='..')){
    throw new TypeError('Invalid private media path');
  }
  if(segments[0]!==owner)throw new TypeError('Private media path is outside the owner scope');
  return value;
}

export function createPrivateMediaStorage({
  client,
  signedUrlTtlSeconds=3600
}={}){
  if(!client?.storage?.from)throw new TypeError('Supabase Storage client is required');
  const bucket=client.storage.from(PRIVATE_MEDIA_BUCKET);
  const ttl=Number(signedUrlTtlSeconds);
  if(!Number.isInteger(ttl)||ttl<60||ttl>86400){
    throw new TypeError('signedUrlTtlSeconds must be between 60 and 86400');
  }

  async function upload({userId,path,file,mimeType,sizeBytes,upsert=false}={}){
    const owned=assertOwnedPrivateMediaPath(path,{userId});
    const image=validatePrivateMediaImage({mimeType,sizeBytes});
    if(file==null)throw new TypeError('Photo file is required');
    const {error}=await bucket.upload(owned,file,{
      upsert:Boolean(upsert),
      contentType:image.mimeType,
      cacheControl:'3600'
    });
    if(error)throw safeError(error,'PRIVATE_MEDIA_UPLOAD_FAILED');
    return Object.freeze({ok:true,path:owned,mimeType:image.mimeType,sizeBytes:image.sizeBytes});
  }

  async function remove({userId,paths}={}){
    const list=Array.isArray(paths)?paths:[paths];
    const owned=list.filter(Boolean).map(path=>assertOwnedPrivateMediaPath(path,{userId}));
    if(!owned.length)return Object.freeze({ok:true,status:'nothing_to_remove',paths:Object.freeze([])});
    const {error}=await bucket.remove(owned);
    if(error)throw safeError(error,'PRIVATE_MEDIA_DELETE_FAILED');
    return Object.freeze({ok:true,status:'deleted',paths:Object.freeze([...owned])});
  }

  async function signedUrl({userId,path}={}){
    const owned=assertOwnedPrivateMediaPath(path,{userId});
    const {data,error}=await bucket.createSignedUrl(owned,ttl);
    if(error)throw safeError(error,'PRIVATE_MEDIA_URL_FAILED');
    const url=String(data?.signedUrl||data?.signedURL||'').trim();
    if(!url)throw new Error('PRIVATE_MEDIA_URL_EMPTY');
    return url;
  }

  return Object.freeze({upload,remove,signedUrl});
}
